import { useId, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { FileDown, ImageDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * A check-in QR code, for whatever is being checked into.
 *
 * The code is rendered at the chosen resolution and displayed scaled down, so
 * what downloads is print-quality without a second hidden canvas: both exports
 * read `canvas.toDataURL()`, which uses the canvas's intrinsic size rather than
 * however large the element happens to be drawn.
 */

const PREVIEW_SIZE = 256;
const SIZE_OPTIONS = [256, 512, 1024, 2048, 4096];

export function QrCodeDialog({
  title,
  caption,
  url,
  fileBase,
  open,
  onOpenChange,
}: {
  title: string;
  /** Printed above the code on the PDF — the thing being checked into. */
  caption: string;
  url: string;
  /** Stem of the downloaded file name; the extension is added per format. */
  fileBase: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [size, setSize] = useState(1024);
  // Two of these can be mounted at once on a page, so the id cannot be a
  // module constant — both canvases would answer to the same one.
  const canvasId = useId();

  const canvas = () =>
    document.getElementById(canvasId) as HTMLCanvasElement | null;

  function saveAsImage() {
    const element = canvas();
    if (!element) return;
    const link = document.createElement('a');
    link.download = `${fileBase}.png`;
    link.href = element.toDataURL('image/png');
    link.click();
  }

  function saveAsPdf() {
    const element = canvas();
    if (!element) return;
    const image = element.toDataURL('image/png');
    const doc = new jsPDF();
    const width = doc.internal.pageSize.getWidth();
    const boxSize = 100;

    doc.setFontSize(14);
    doc.text(caption, width / 2, 25, { align: 'center' });
    doc.addImage(image, 'PNG', (width - boxSize) / 2, 35, boxSize, boxSize);
    doc.setFontSize(10);
    doc.text(url, width / 2, 145, { align: 'center' });
    doc.save(`${fileBase}.pdf`);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-scrim="over"
          className={cn(
            'fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'bg-background fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border p-4 shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <DialogPrimitive.Title className="pr-8 text-sm font-semibold">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-muted-foreground mt-0.5 pr-8 text-xs break-all">
            {url}
          </DialogPrimitive.Description>
          <DialogPrimitive.Close className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-3 right-3 rounded p-1 focus-visible:ring-2 focus-visible:outline-none">
            <X className="size-4" />
            <span className="sr-only">{t('orders.close')}</span>
          </DialogPrimitive.Close>

          <div className="my-4 flex justify-center">
            <QRCodeCanvas
              id={canvasId}
              value={url}
              size={size}
              level="M"
              includeMargin
              style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">
                {t('events.resolution')}
              </span>
              <Select
                value={String(size)}
                onValueChange={(value) => setSize(Number(value))}
              >
                <SelectTrigger className="h-8 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option} × {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stacked full width below sm: three buttons in a row ran past
                the edge at 360px. */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="flex-1"
                onClick={saveAsImage}
              >
                <ImageDown />
                {t('events.saveAsImage')}
              </Button>
              <Button variant="outline" className="flex-1" onClick={saveAsPdf}>
                <FileDown />
                {t('events.saveAsPdf')}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
